// FILE: src/Components/Copilot/CopilotGoalCard.js
import React, {
  useMemo,
  useState,
} from "react";

const TERMINAL_GOAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
]);

const GOAL_STATUS_DISPLAY = {
  planning: {
    label: "Planning",
    icon: "fa-solid fa-wand-magic-sparkles",
    badge: "bg-violet-400/12 text-violet-100/75",
    border: "border-violet-300/[0.13]",
  },
  running: {
    label: "In progress",
    icon: "fa-solid fa-spinner fa-spin",
    badge: "bg-blue-400/12 text-blue-100/75",
    border: "border-blue-300/[0.13]",
  },
  awaiting_input: {
    label: "Waiting for input",
    icon: "fa-solid fa-message",
    badge: "bg-amber-400/12 text-amber-100/75",
    border: "border-amber-300/[0.13]",
  },
  awaiting_review: {
    label: "Ready for review",
    icon: "fa-solid fa-list-check",
    badge: "bg-violet-400/12 text-violet-100/75",
    border: "border-violet-300/[0.13]",
  },
  awaiting_confirmation: {
    label: "Waiting for confirmation",
    icon: "fa-solid fa-circle-question",
    badge: "bg-amber-400/12 text-amber-100/75",
    border: "border-amber-300/[0.13]",
  },
  executing: {
    label: "Executing",
    icon: "fa-solid fa-spinner fa-spin",
    badge: "bg-blue-400/12 text-blue-100/75",
    border: "border-blue-300/[0.13]",
  },
  partially_completed: {
    label: "Partially completed",
    icon: "fa-solid fa-triangle-exclamation",
    badge: "bg-amber-400/12 text-amber-100/75",
    border: "border-amber-300/[0.13]",
  },
  completed: {
    label: "Completed",
    icon: "fa-solid fa-circle-check",
    badge: "bg-emerald-400/12 text-emerald-100/75",
    border: "border-emerald-300/[0.13]",
  },
  failed: {
    label: "Failed",
    icon: "fa-solid fa-circle-exclamation",
    badge: "bg-red-400/12 text-red-100/75",
    border: "border-red-300/[0.13]",
  },
  cancelled: {
    label: "Cancelled",
    icon: "fa-solid fa-ban",
    badge: "bg-white/[0.06] text-white/45",
    border: "border-white/[0.08]",
  },
};

const STEP_STATUS_DISPLAY = {
  pending: {
    icon: "fa-regular fa-circle",
    color: "text-white/25",
  },
  ready: {
    icon: "fa-solid fa-circle-dot",
    color: "text-violet-200/65",
  },
  running: {
    icon: "fa-solid fa-spinner fa-spin",
    color: "text-blue-200/75",
  },
  awaiting_input: {
    icon: "fa-solid fa-message",
    color: "text-amber-200/75",
  },
  awaiting_review: {
    icon: "fa-solid fa-list-check",
    color: "text-violet-200/75",
  },
  awaiting_confirmation: {
    icon: "fa-solid fa-circle-question",
    color: "text-amber-200/75",
  },
  completed: {
    icon: "fa-solid fa-circle-check",
    color: "text-emerald-200/75",
  },
  failed: {
    icon: "fa-solid fa-circle-exclamation",
    color: "text-red-200/75",
  },
  skipped: {
    icon: "fa-solid fa-forward",
    color: "text-white/30",
  },
  cancelled: {
    icon: "fa-solid fa-ban",
    color: "text-white/30",
  },
};

const formatLabel = (value = "") => {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
};

const getAssumptionText = (
  assumption
) => {
  if (
    typeof assumption === "string"
  ) {
    return assumption;
  }

  if (
    !assumption ||
    typeof assumption !== "object"
  ) {
    return null;
  }

  if (assumption.reason) {
    return assumption.reason;
  }

  if (
    assumption.field &&
    assumption.value !== undefined &&
    assumption.value !== null
  ) {
    const value =
      typeof assumption.value ===
      "object"
        ? JSON.stringify(
            assumption.value
          )
        : String(
            assumption.value
          );

    return `${formatLabel(
      assumption.field
    )}: ${value}`;
  }

  return null;
};

export default function CopilotGoalCard({
  goal,
  loading = false,
  onCancel,
}) {
  const [
    expanded,
    setExpanded,
  ] = useState(false);

  const steps =
    Array.isArray(goal?.steps)
      ? goal.steps
      : [];

  const assumptions =
    Array.isArray(
      goal?.assumptions
    )
      ? goal.assumptions
          .map(getAssumptionText)
          .filter(Boolean)
      : [];

  const completedCount =
    steps.filter((step) =>
      [
        "completed",
        "skipped",
      ].includes(step.status)
    ).length;

  const failedCount =
    steps.filter(
      (step) =>
        step.status === "failed"
    ).length;

  const progressPercentage =
    steps.length > 0
      ? Math.round(
          (completedCount /
            steps.length) *
            100
        )
      : 0;

  const status =
    String(
      goal?.status || "planning"
    ).toLowerCase();

  const statusDisplay =
    GOAL_STATUS_DISPLAY[status] ||
    GOAL_STATUS_DISPLAY.planning;

  const activeStep =
    steps.find(
      (step) =>
        step.stepId ===
        goal?.currentStepId
    ) ||
    steps.find((step) =>
      [
        "running",
        "awaiting_input",
        "awaiting_review",
        "awaiting_confirmation",
      ].includes(step.status)
    ) ||
    null;

  const canCancel =
    Boolean(
      goal?.id || goal?._id
    ) &&
    !TERMINAL_GOAL_STATUSES.has(
      status
    );

  const visibleSteps =
    useMemo(() => {
      if (expanded) {
        return steps;
      }

      const importantSteps =
        steps.filter((step) => {
          return (
            step.stepId ===
              goal?.currentStepId ||
            [
              "running",
              "awaiting_input",
              "awaiting_review",
              "awaiting_confirmation",
              "failed",
            ].includes(step.status)
          );
        });

      if (importantSteps.length > 0) {
        return importantSteps.slice(
          0,
          3
        );
      }

      return steps.slice(-3);
    }, [
      expanded,
      goal?.currentStepId,
      steps,
    ]);

  if (!goal) {
    return null;
  }

  return (
    <section
      className={`overflow-hidden rounded-2xl border bg-gradient-to-b from-violet-400/[0.055] to-black/[0.1] shadow-[0_16px_45px_rgba(0,0,0,0.18)] ${statusDisplay.border}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-400/15 text-violet-100">
              <i className="fa-solid fa-bullseye text-sm"></i>
            </span>

            <div className="min-w-0">
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-violet-100/55">
                Copilot Goal
              </span>

              <h3 className="mb-0 line-clamp-2 text-sm font-black leading-relaxed text-white/90 sm:text-base">
                {goal.objective ||
                  "Complete the Copilot request"}
              </h3>
            </div>
          </div>

          {activeStep && (
            <p className="mb-0 text-xs leading-relaxed text-white/42">
              {activeStep.title ||
                activeStep.objective ||
                "Working on the next goal step."}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <span
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${statusDisplay.badge}`}
          >
            <i
              className={`${statusDisplay.icon} text-[9px]`}
            ></i>
            {statusDisplay.label}
          </span>

          {canCancel && (
            <button
              type="button"
              disabled={loading}
              onClick={() =>
                onCancel?.(goal)
              }
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-bold text-white/50 transition hover:bg-red-400/12 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i
                className={`fa-solid ${
                  loading
                    ? "fa-spinner fa-spin"
                    : "fa-xmark"
                } text-[9px]`}
              ></i>
              {loading
                ? "Cancelling..."
                : "Cancel goal"}
            </button>
          )}
        </div>
      </div>

      <div className="border-t border-white/[0.06] px-4 py-3">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
            Progress
          </span>

          <span className="text-[10px] font-black text-white/55">
            {completedCount} /{" "}
            {steps.length} steps
          </span>
        </div>

        <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.07]">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              status === "failed"
                ? "bg-red-400"
                : status ===
                  "partially_completed"
                ? "bg-amber-300"
                : "bg-violet-300"
            }`}
            style={{
              width: `${Math.min(
                Math.max(
                  progressPercentage,
                  0
                ),
                100
              )}%`,
            }}
          ></div>
        </div>
      </div>

      {visibleSteps.length > 0 && (
        <div className="space-y-2 border-t border-white/[0.06] px-4 py-3">
          {visibleSteps.map((step) => {
            const stepDisplay =
              STEP_STATUS_DISPLAY[
                step.status
              ] ||
              STEP_STATUS_DISPLAY.pending;

            return (
              <div
                key={step.stepId}
                className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                  step.stepId ===
                  goal.currentStepId
                    ? "border-violet-300/20 bg-violet-400/[0.07]"
                    : "border-white/[0.055] bg-black/[0.1]"
                }`}
              >
                <span
                  className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/[0.045] ${stepDisplay.color}`}
                >
                  <i
                    className={`${stepDisplay.icon} text-[9px]`}
                  ></i>
                </span>

                <div className="min-w-0 flex-1">
                  <p className="mb-0 text-xs font-bold text-white/72">
                    {step.title ||
                      formatLabel(
                        step.type
                      )}
                  </p>

                  {step.objective && (
                    <p className="mb-0 mt-0.5 line-clamp-2 text-[10px] leading-relaxed text-white/34">
                      {step.objective}
                    </p>
                  )}

                  {step.error?.message && (
                    <p className="mb-0 mt-1 text-[10px] leading-relaxed text-red-200/65">
                      {step.error.message}
                    </p>
                  )}
                </div>

                <span className="flex-shrink-0 text-[9px] font-semibold text-white/28">
                  {formatLabel(
                    step.status
                  )}
                </span>
              </div>
            );
          })}

          {steps.length > 3 && (
            <button
              type="button"
              onClick={() =>
                setExpanded(
                  (current) =>
                    !current
                )
              }
              className="inline-flex h-8 items-center gap-2 rounded-lg bg-white/[0.045] px-3 text-[10px] font-bold text-white/45 transition hover:bg-white/[0.08] hover:text-white"
            >
              <i
                className={`fa-solid fa-chevron-down text-[8px] transition-transform ${
                  expanded
                    ? "rotate-180"
                    : ""
                }`}
              ></i>
              {expanded
                ? "Show less"
                : `Show all ${steps.length} steps`}
            </button>
          )}
        </div>
      )}

      {assumptions.length > 0 && (
        <details className="group border-t border-white/[0.06]">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
            <span className="text-[10px] font-semibold text-white/35">
              Assumptions made
            </span>

            <i className="fa-solid fa-chevron-down text-[8px] text-white/25 transition-transform group-open:rotate-180"></i>
          </summary>

          <div className="space-y-1.5 border-t border-white/[0.04] px-4 py-3">
            {assumptions.map(
              (assumption, index) => (
                <div
                  key={`${assumption}-${index}`}
                  className="flex items-start gap-2 text-[10px] leading-relaxed text-white/42"
                >
                  <i className="fa-solid fa-circle-info mt-0.5 text-[9px] text-violet-200/50"></i>
                  <span>
                    {assumption}
                  </span>
                </div>
              )
            )}
          </div>
        </details>
      )}

      {failedCount > 0 && (
        <div className="border-t border-red-300/[0.08] bg-red-400/[0.04] px-4 py-3">
          <p className="mb-0 text-[10px] font-semibold text-red-100/65">
            {failedCount} goal step
            {failedCount === 1
              ? ""
              : "s"}{" "}
            failed. Available results may
            still be shown below.
          </p>
        </div>
      )}
    </section>
  );
}